import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTheme } from "../../../hooks/useTheme";
import NoChartDataView from "../no-chart-data-view/no-chart-data-view";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

interface IProps {
  data1: string[] | number[];
  data2?: string[] | number[];
  data3?: string[] | number[];
  data4?: string[] | number[];
  dataLabel1?: string;
  dataLabel2?: string;
  dataLabel3?: string;
  dataLabel4?: string;
  borderColor1: string | string[];
  borderColor2?: string;
  borderColor3?: string;
  borderColor4?: string;
  backgroundColor1: string | string[];
  backgroundColor2?: string;
  backgroundColor3?: string;
  backgroundColor4?: string;
  yAxesUnit: string;
  labels: Array<string>;
  stacked?: boolean;
  hasSecondDataSet?: boolean;
  hasThirdDataSet?: boolean;
  hasForthDataSet?: boolean;
  borderRadius?: number;
  borderSkipped?: any;
  barThickness?: number;
  isDisplayLegend?: boolean;
  titleCallBack?: (tooltipItems: any) => string;
  labelCallback?: (context: TooltipItem<"bar">) => string | string[];
  isChartDataAvailable?: boolean;
  height?: string;
  minHeight?: string;
  width?: string;
  displayGridLines?: boolean;
  autoSkipX?: boolean;
  xTickFontSize?: number;
  xAxisTitle?: string;
}

const BarChart = (props: IProps) => {
  const effectiveTheme = useTheme();

  const getThemeColor = (varName: string, fallback: string) => {
    if (typeof window === "undefined") return fallback;
    const computed = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return computed || fallback;
  };

  const resolveColor = (
    color: string | string[] | undefined,
    fallback: string,
  ): string | string[] => {
    if (!color) return fallback;
    if (Array.isArray(color)) {
      return color.map((c) => resolveColor(c, fallback) as string);
    }
    if (color.startsWith("var(")) {
      const varName = color.replace(/^var\((--[^,\s)]+).*\)$/, "$1");
      const resolved = getThemeColor(varName, fallback);
      return resolved || fallback;
    }
    return color;
  };

  const tickColor = getThemeColor(
    "--light",
    effectiveTheme === "dark" ? "#91a4c2" : "#69768b",
  );
  const gridColor = getThemeColor(
    "--line",
    effectiveTheme === "dark" ? "#2b2697" : "#ecf1ff",
  );
  const tooltipBg = getThemeColor(
    "--card-bg",
    effectiveTheme === "dark" ? "#3731a6" : "#ffffff",
  );
  const tooltipTitleColor = getThemeColor(
    "--dark",
    effectiveTheme === "dark" ? "#ffffff" : "#231f20",
  );
  const tooltipBodyColor = getThemeColor(
    "--light",
    effectiveTheme === "dark" ? "#91a4c2" : "#69768b",
  );

  const bg1 = resolveColor(
    props.backgroundColor1,
    getThemeColor("--icon-color", "#6b72cd"),
  );
  const border1 = resolveColor(
    props.borderColor1,
    getThemeColor("--icon-color", "#6b72cd"),
  );
  const bg2 = resolveColor(
    props.backgroundColor2,
    getThemeColor("--primary", "#7483CC"),
  );
  const border2 = resolveColor(
    props.borderColor2,
    getThemeColor("--primary", "#7483CC"),
  );
  const bg3 = resolveColor(props.backgroundColor3, "#3ecd7b");
  const border3 = resolveColor(props.borderColor3, "#3ecd7b");
  const bg4 = resolveColor(props.backgroundColor4, "#d84560");
  const border4 = resolveColor(props.borderColor4, "#d84560");

  let labels = props.labels;
  let adjustedData1 = props.data1;
  let adjustedData2 = props.data2;
  let adjustedData3 = props.data3;
  let adjustedData4 = props.data4;

  const minBars = 8;
  if (labels.length > 0 && labels.length < minBars) {
    const diff = minBars - labels.length;
    labels = [...labels, ...Array(diff).fill("")];
    if (adjustedData1)
      adjustedData1 = [...adjustedData1, ...Array(diff).fill(null)] as any;
    if (adjustedData2)
      adjustedData2 = [...adjustedData2, ...Array(diff).fill(null)] as any;
    if (adjustedData3)
      adjustedData3 = [...adjustedData3, ...Array(diff).fill(null)] as any;
    if (adjustedData4)
      adjustedData4 = [...adjustedData4, ...Array(diff).fill(null)] as any;
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: props.isDisplayLegend
        ? {
            display: true,
            position: "top" as const,
            align: "start" as const,
            labels: {
              usePointStyle: true,
              pointStyleWidth: 10,
              boxHeight: 7,
              font: {
                size: 14,
                family: "Poppins",
                weight: "normal" as const,
              },
              color: tickColor,
            },
          }
        : { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: tooltipTitleColor,
        bodyColor: tooltipBodyColor,
        callbacks: {
          label: props.labelCallback
            ? props.labelCallback
            : (context: TooltipItem<"bar">) => {
                let label = "";
                if (context.parsed.y) {
                  if (props.yAxesUnit === "Rs.") {
                    label = props.yAxesUnit + " " + context.parsed.y.toFixed(2);
                  } else {
                    label = context.parsed.y.toFixed(2) + " " + props.yAxesUnit;
                  }
                }
                return label;
              },
          title: props.titleCallBack,
        },
      },
      datalabels: {
        display: false,
      },
      centerText: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          color: tickColor,
          autoSkip: props.autoSkipX !== false,
          font: {
            size: props.xTickFontSize || 12,
            family: "Poppins",
          },
          maxRotation: 0,
          minRotation: 0,
        },
        title: props.xAxisTitle
          ? {
              display: true,
              text: props.xAxisTitle,
              color: tickColor,
              font: {
                size: 12,
                family: "Poppins",
              },
            }
          : { display: false },
        stacked: props.stacked,
      },
      y: {
        beginAtZero: true,
        grid: {
          display: props.displayGridLines !== false,
          color: gridColor,
        },
        ticks: {
          display: true,
          color: tickColor,
          callback: (value: any) => {
            return value + " " + props.yAxesUnit;
          },
          font: {
            size: 12,
            family: "Poppins",
          },
        },

        stacked: props.stacked,
      },
    },
  };

  const legendMargin = {
    id: "legendMargin",
    beforeInit: (chart: any) => {
      const fitValue = chart.legend.fit;
      chart.legend.fit = () => {
        fitValue.bind(chart.legend)();
        return (chart.legend.height += 30);
      };
    },
  };

  const data1 = {
    labels: labels,
    datasets: [
      {
        data: adjustedData1,
        backgroundColor: bg1,
        borderWidth: 1,
        borderColor: border1,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel1 || "",
      },
    ],
  };

  const data2 = {
    labels: labels,
    datasets: [
      {
        data: adjustedData1,
        backgroundColor: bg1,
        borderWidth: 1,
        borderColor: border1,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel1 || "",
      },
      {
        data: adjustedData2,
        backgroundColor: bg2,
        borderWidth: 1,
        borderColor: border2,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel2 || "",
      },
    ],
  };

  const data3 = {
    labels: labels,
    datasets: [
      {
        data: adjustedData1,
        backgroundColor: bg1,
        borderWidth: 1,
        borderColor: border1,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel1 || "",
      },
      {
        data: adjustedData2,
        backgroundColor: bg2,
        borderWidth: 1,
        borderColor: border2,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel2 || "",
      },
      {
        data: adjustedData3,
        backgroundColor: bg3,
        borderWidth: 1,
        borderColor: border3,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel3 || "",
      },
    ],
  };

  const data4 = {
    labels: labels,
    datasets: [
      {
        data: adjustedData1,
        backgroundColor: bg1,
        borderWidth: 1,
        borderColor: border1,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel1 || "",
      },
      {
        data: adjustedData2,
        backgroundColor: bg2,
        borderWidth: 1,
        borderColor: border2,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel2 || "",
      },
      {
        data: adjustedData3,
        backgroundColor: bg3,
        borderWidth: 1,
        borderColor: border3,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel3 || "",
      },
      {
        data: adjustedData4,
        backgroundColor: bg4,
        borderWidth: 1,
        borderColor: border4,
        borderRadius: props.borderRadius || 0,
        borderSkipped: props.borderSkipped,
        barThickness: props.barThickness,
        label: props.dataLabel4 || "",
      },
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        height: props.height || "100%",
      }}
    >
      <div
        style={{
          minWidth: props.isChartDataAvailable
            ? props.width
              ? "100%"
              : "calc(100vw - 350px)"
            : "100%",
          width: props.width || "1000px",
          height: props.height ? "100%" : undefined,
          minHeight: props.height
            ? undefined
            : props.minHeight ||
              (props.isChartDataAvailable
                ? "calc(100vh - 300px)"
                : "calc(100vh - 190px)"),
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        {props.isChartDataAvailable ? (
          <Bar
            data={
              props.hasForthDataSet
                ? data4
                : props.hasThirdDataSet
                  ? data3
                  : props.hasSecondDataSet
                    ? data2
                    : data1
            }
            options={options}
            plugins={props.isDisplayLegend ? [legendMargin] : []}
          />
        ) : (
          <NoChartDataView />
        )}
      </div>
    </div>
  );
};

export default BarChart;
